// "Choose a branch" in front of the public booking page.
//
// A hospital group shares one WhatsApp number, so the bot's link opens the
// group's root (the number owner). When the group has more than one bookable
// branch, the patient picks one here FIRST, and the booking page then opens for
// that branch -- it exchanges the one-time WhatsApp link there, so the session,
// doctors and settings are all the branch's. `branch=1` in the URL marks the
// choice as made. Any failure falls through to the booking page as before.
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiChevronRight, FiMapPin } from "react-icons/fi";
import PublicBooking from "./PublicBooking";
import { getPublicHospitalBranches, PublicBranch } from "../lib/apis/publicBooking";

type Gate =
  | { state: "loading" }
  | { state: "book" }
  | { state: "pick"; branches: PublicBranch[]; groupName: string };

const PublicBranchGate: React.FC = () => {
  const { hospital_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const chosen = new URLSearchParams(location.search).get("branch") === "1";
  const [gate, setGate] = useState<Gate>({ state: chosen ? "book" : "loading" });

  const openBranch = (branchId: string) => {
    const q = new URLSearchParams(location.search);
    q.set("branch", "1");
    navigate(`/${branchId}?${q.toString()}${location.hash}`, { replace: true });
  };

  useEffect(() => {
    if (!hospital_id || chosen) {
      setGate({ state: "book" });
      return;
    }
    let cancelled = false;
    setGate({ state: "loading" });
    getPublicHospitalBranches(hospital_id)
      .then(({ root_id, root_name, branches }) => {
        if (cancelled) return;
        // A link to one branch directly, or a hospital without branches.
        if (hospital_id !== root_id || branches.length === 0) return setGate({ state: "book" });
        // Root is a placeholder with a single real branch: go straight there.
        if (branches.length === 1) {
          if (branches[0].hospital_id === hospital_id) return setGate({ state: "book" });
          return openBranch(branches[0].hospital_id);
        }
        // Older backends send no root_name; the root's own row is the fallback.
        const root = branches.find((b) => b.hospital_id === root_id);
        setGate({ state: "pick", branches, groupName: (root_name || (root ? root.name : "")).trim() });
      })
      .catch(() => {
        if (!cancelled) setGate({ state: "book" });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospital_id, chosen]);

  if (gate.state === "book") return <PublicBooking />;
  if (gate.state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-sm text-queueText">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 md:bg-gradient-to-b md:from-[#EEF3FF] md:via-white md:to-[#F4F7FE] flex justify-center px-4 py-8">
      <div className="max-w-md w-full">
        <p className="text-[11px] font-semibold tracking-[0.18em] text-[#0F3CBE] uppercase">Online booking</p>
        {gate.groupName && (
          <p className="mt-2 text-sm text-queueText">
            Welcome to <span className="font-semibold text-modalText">{gate.groupName}</span>
          </p>
        )}
        <h1 className="mt-1 text-2xl font-bold text-modalText">Choose a branch</h1>
        <p className="mt-1 text-sm text-queueText">
          {gate.branches.length} branches. Pick the one you want to visit.
        </p>
        <div className="mt-5 flex flex-col gap-2.5">
          {gate.branches.map((b) => (
            <button
              key={b.hospital_id}
              type="button"
              onClick={() => openBranch(b.hospital_id)}
              className="w-full flex items-center justify-between gap-3 rounded-xl border border-doctorsBorder bg-white px-4 py-3.5 text-left hover:border-[#0F3CBE] hover:bg-[#F3F6FF] transition-colors"
            >
              <span className="min-w-0">
                <span className="block text-[15px] font-semibold text-modalText truncate">{b.name}</span>
                {b.address && (
                  <span className="mt-0.5 flex items-start gap-1 text-[12px] text-queueText">
                    <FiMapPin className="w-3.5 h-3.5 mt-[1px] shrink-0" />
                    <span className="line-clamp-2">{b.address}</span>
                  </span>
                )}
              </span>
              <FiChevronRight className="w-5 h-5 shrink-0 text-queueText" />
            </button>
          ))}
        </div>
        <p className="mt-6 text-center text-[12px] text-queueText">
          Powered by <span className="font-semibold text-[#0F3CBE]">DocTrue</span>
        </p>
      </div>
    </div>
  );
};

export default PublicBranchGate;
